import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { QcField } from '@nucleus-commerce/api-client/admin';

import { QcFieldInput, type QcAnswer } from './qc-field-input';

export interface QcFormPreviewProps {
  fields: QcField[];
}

/** The checklist rendered live from the builder's values; answers stay in this component. */
export function QcFormPreview({ fields }: QcFormPreviewProps) {
  const { t } = useTranslation('workflows');
  const [answers, setAnswers] = useState<Record<string, QcAnswer>>({});

  if (fields.length === 0) {
    return <p className="text-muted-foreground text-sm">{t('qc.previewEmpty')}</p>;
  }

  return (
    <ol className="flex flex-col gap-4" aria-label={t('qc.previewTitle')} data-testid="qc-preview">
      {fields.map((field, index) => {
        const slot = `${index}:${field.type}`;
        return (
          <li key={`${index}-${field.key}`} className="flex flex-col gap-2">
            <p className="text-sm font-medium">
              {field.label || t('qc.field.untitled')}
              {field.required ? (
                <span className="text-destructive ml-1" aria-label={t('qc.answer.required')}>
                  *
                </span>
              ) : null}
            </p>
            {field.prefill ? (
              <p className="text-muted-foreground text-xs">
                {t('qc.answer.prefilled')} · {t(`qc.prefill.${field.prefill}`)}
              </p>
            ) : null}
            <QcFieldInput
              key={slot}
              field={{ ...field, label: field.label || t('qc.field.untitled') }}
              value={answers[slot] ?? null}
              onChange={(value) => setAnswers((current) => ({ ...current, [slot]: value }))}
            />
            {field.type === 'file' ? (
              <p className="text-muted-foreground text-xs">{t('qc.field.fileHint')}</p>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
